// PR-AT hotfix: useState added to import. PR-AT introduced
// `useState<RecentTypeFilter>('all')` for the recent-activity pill filter
// but missed adding the import. With tsconfig strict:false TypeScript
// didn't catch the missing identifier — surfaced as runtime
// "useState is not defined" on /manager/dashboard.
// Sprint F: tsconfig → strict mode would catch this class at compile-time.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CalendarDays,
  TrendingUp,
  TrendingDown,
  CreditCard,
  BarChart3,
  Plus,
  CalendarRange,
  ChevronRight,
  Minus,
} from "lucide-react";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { sv } from "date-fns/locale";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useOrganization } from "@/hooks/useOrganization";
import { useUnpaidCount } from "@/hooks/useUnpaidPayments";

function TrendBadge({ value, label }: { value: number; label: string }) {
  if (value > 0)
    return (
      <span className="text-xs text-green-600 flex items-center gap-0.5">
        <TrendingUp className="h-3 w-3" /> +{value} {label}
      </span>
    );
  if (value < 0)
    return (
      <span className="text-xs text-red-600 flex items-center gap-0.5">
        <TrendingDown className="h-3 w-3" /> {value} {label}
      </span>
    );
  return (
    <span className="text-xs text-muted-foreground flex items-center gap-0.5">
      <Minus className="h-3 w-3" /> 0 {label}
    </span>
  );
}

export default function ManagerDashboard() {
  const { t } = useTranslation("admin");
  const today = new Date().toISOString().split("T")[0];
  const yesterday = format(subDays(new Date(), 1), "yyyy-MM-dd");
  const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");
  const monthEnd = format(endOfMonth(new Date()), "yyyy-MM-dd");
  const prevMonthStart = format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
  const prevMonthEnd = format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
  const todayDow = new Date().getDay();

  const { courts, hall, hallId } = useOrgHall();
  const { organization } = useOrganization();

  // --- Bookings today (with hall filter) ---
  const { data: bookingsToday = 0 } = useQuery({
    queryKey: ["mgr-bookings-today", hallId],
    queryFn: async () => {
      const { count } = await supabase
        .from("bookings")
        .select("*, courts!inner(hall_id)", { count: "exact", head: true })
        .eq("courts.hall_id", hallId!)
        .eq("status", "confirmed")
        .gte("start_time", today + "T00:00:00")
        .lt("start_time", today + "T23:59:59");
      return count ?? 0;
    },
    enabled: !!hallId,
  });

  const { data: bookingsYesterday = 0 } = useQuery({
    queryKey: ["mgr-bookings-yesterday", hallId],
    queryFn: async () => {
      const { count } = await supabase
        .from("bookings")
        .select("*, courts!inner(hall_id)", { count: "exact", head: true })
        .eq("courts.hall_id", hallId!)
        .eq("status", "confirmed")
        .gte("start_time", yesterday + "T00:00:00")
        .lt("start_time", yesterday + "T23:59:59");
      return count ?? 0;
    },
    enabled: !!hallId,
  });

  // --- Subscriptions today ---
  const { data: subscriptionsToday = [] } = useQuery({
    queryKey: ["mgr-subscriptions-today", hallId, today],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_bookings")
        .select("id, court_id, start_time, end_time, price, valid_from, valid_until, interval_weeks, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .eq("day_of_week", todayDow)
        .eq("status", "active")
        .lte("valid_from", today)
        .gte("valid_until", today);
      return (data ?? []).filter((rb: any) => {
        if (!rb.interval_weeks || rb.interval_weeks === 1) return true;
        const fromDate = new Date(rb.valid_from);
        const currentDate = new Date(today);
        const diffWeeks = Math.round((currentDate.getTime() - fromDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
        return diffWeeks % rb.interval_weeks === 0;
      });
    },
    enabled: !!hallId,
  });

  const { data: releasesToday = [] } = useQuery({
    queryKey: ["mgr-releases-today", organization?.id, today],
    queryFn: async () => {
      const { data } = await supabase
        .from("subscription_releases" as any)
        .select("subscription_id, status")
        .eq("organization_id", organization!.id)
        .eq("release_date", today)
        .in("status", ["released", "rebooked"]);
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const releasedIds = new Set(
    ((releasesToday ?? []) as any[]).filter((r) => r.status === "released").map((r) => r.subscription_id)
  );
  const activeSubscriptionsToday = subscriptionsToday.filter((s: any) => !releasedIds.has(s.id));
  const totalBookingsToday = bookingsToday + activeSubscriptionsToday.length;

  // --- Unpaid ---
  // UPPDRAG-20: switched to shared canonical hook so dashboard count
  // matches the list-page count (DashboardUnpaid.tsx). The previous
  // inline query counted ALL pending booking_payments incl. those
  // tied to cancelled bookings + walk-in admin-cash bookings,
  // over-counting by ~32% in prod.
  const { count: unpaidCount } = useUnpaidCount(hallId);

  // --- Revenue this month (bookings.price_total + subscription occurrences) ---
  const { data: monthRevenue = 0 } = useQuery({
    queryKey: ["mgr-month-revenue", hallId, monthStart, monthEnd],
    queryFn: async () => {
      // Sprint D aggregates: prefer price_total_minor.
      const { data: bookings } = await supabase
        .from("bookings")
        .select("price_total, price_total_minor, currency, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .eq("status", "confirmed")
        .gte("start_time", monthStart + "T00:00:00")
        .lte("start_time", monthEnd + "T23:59:59");
      const bookingRevenueMinor = (bookings ?? []).reduce(
        (sum: number, b: any) => sum + (resolveMoney(b.price_total_minor, b.price_total, b.currency)?.valueMinor ?? 0), 0);

      // Subscription revenue: count occurrences in month — Sprint D dual-state.
      const { data: subs } = await supabase
        .from("recurring_bookings")
        .select("price, price_minor, currency, day_of_week, valid_from, valid_until, interval_weeks, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .eq("status", "active")
        .lte("valid_from", monthEnd)
        .gte("valid_until", monthStart);

      let subRevenueMinor = 0;
      for (const sub of (subs ?? []) as any[]) {
        const subStart = new Date(Math.max(new Date(sub.valid_from).getTime(), new Date(monthStart).getTime()));
        const subEnd = new Date(Math.min(new Date(sub.valid_until).getTime(), new Date(monthEnd).getTime()));
        const cursor = new Date(subStart);
        while (cursor.getDay() !== sub.day_of_week && cursor <= subEnd) cursor.setDate(cursor.getDate() + 1);
        const interval = sub.interval_weeks || 1;
        const subMinor = resolveMoney(sub.price_minor, sub.price, sub.currency)?.valueMinor ?? 0;
        while (cursor <= subEnd) {
          subRevenueMinor += subMinor;
          cursor.setDate(cursor.getDate() + 7 * interval);
        }
      }
      return Math.round((bookingRevenueMinor + subRevenueMinor) / 100);  // kronor for downstream display
    },
    enabled: !!hallId,
  });

  const { data: prevMonthRevenue = 0 } = useQuery({
    queryKey: ["mgr-prev-month-revenue", hallId, prevMonthStart, prevMonthEnd],
    queryFn: async () => {
      // Sprint D aggregates: prefer price_total_minor.
      const { data: bookings } = await supabase
        .from("bookings")
        .select("price_total, price_total_minor, currency, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .eq("status", "confirmed")
        .gte("start_time", prevMonthStart + "T00:00:00")
        .lte("start_time", prevMonthEnd + "T23:59:59");
      const bookingRevenueMinor = (bookings ?? []).reduce(
        (sum: number, b: any) => sum + (resolveMoney(b.price_total_minor, b.price_total, b.currency)?.valueMinor ?? 0), 0);

      const { data: subs } = await supabase
        .from("recurring_bookings")
        .select("price, price_minor, currency, day_of_week, valid_from, valid_until, interval_weeks, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .eq("status", "active")
        .lte("valid_from", prevMonthEnd)
        .gte("valid_until", prevMonthStart);

      let subRevenueMinor = 0;
      for (const sub of (subs ?? []) as any[]) {
        const subStart = new Date(Math.max(new Date(sub.valid_from).getTime(), new Date(prevMonthStart).getTime()));
        const subEnd = new Date(Math.min(new Date(sub.valid_until).getTime(), new Date(prevMonthEnd).getTime()));
        const cursor = new Date(subStart);
        while (cursor.getDay() !== sub.day_of_week && cursor <= subEnd) cursor.setDate(cursor.getDate() + 1);
        const interval = sub.interval_weeks || 1;
        const subMinor = resolveMoney(sub.price_minor, sub.price, sub.currency)?.valueMinor ?? 0;
        while (cursor <= subEnd) {
          subRevenueMinor += subMinor;
          cursor.setDate(cursor.getDate() + 7 * interval);
        }
      }
      return Math.round((bookingRevenueMinor + subRevenueMinor) / 100);
    },
    enabled: !!hallId,
  });

  const revenueTrendPct = prevMonthRevenue > 0
    ? Math.round(((monthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100)
    : 0;

  // --- Occupancy today (including subscriptions) ---
  const { data: todayBookingsList = [] } = useQuery({
    queryKey: ["mgr-occupancy-today", hallId, today],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("court_id, duration_minutes, courts!inner(hall_id)")
        .eq("courts.hall_id", hallId!)
        .neq("status", "cancelled")
        .gte("start_time", today + "T00:00:00")
        .lt("start_time", today + "T23:59:59");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hallId,
  });

  const occupancyPct = useMemo(() => {
    const activeCourts = courts.filter((c) => c.status === "active");
    if (activeCourts.length === 0) return null;

    const startH = parseInt(hall?.opening_time?.split(":")[0] ?? "7", 10);
    let endH = parseInt(hall?.closing_time?.split(":")[0] ?? "23", 10);
    if (endH === 0) endH = 24;
    const slotsPerCourt = Math.max(endH - startH, 0);
    const totalSlots = slotsPerCourt * activeCourts.length;
    if (totalSlots === 0) return null;

    const bookingHours = todayBookingsList.reduce(
      (sum: number, b: any) => sum + ((b.duration_minutes ?? 60) / 60),
      0
    );

    const subscriptionHours = activeSubscriptionsToday.reduce((sum: number, sub: any) => {
      if (!sub.start_time || !sub.end_time) return sum;
      const [sh, sm] = sub.start_time.split(":").map(Number);
      const [eh, em] = sub.end_time.split(":").map(Number);
      return sum + ((eh * 60 + em) - (sh * 60 + sm)) / 60;
    }, 0);

    const bookedHours = bookingHours + subscriptionHours;
    return Math.min(Math.round((bookedHours / totalSlots) * 100), 100);
  }, [courts, hall, todayBookingsList, activeSubscriptionsToday]);

  // --- Week chart (with hall filter + subscriptions) ---
  const { data: weekData = [] } = useQuery({
    queryKey: ["mgr-week-chart", hallId],
    queryFn: async () => {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = subDays(new Date(), i);
        const dateStr = format(d, "yyyy-MM-dd");
        const dow = d.getDay();

        const { count: bookingCount } = await supabase
          .from("bookings")
          .select("*, courts!inner(hall_id)", { count: "exact", head: true })
          .eq("courts.hall_id", hallId!)
          .eq("status", "confirmed")
          .gte("start_time", dateStr + "T00:00:00")
          .lt("start_time", dateStr + "T23:59:59");

        const { count: subCount } = await supabase
          .from("recurring_bookings")
          .select("*, courts!inner(hall_id)", { count: "exact", head: true })
          .eq("courts.hall_id", hallId!)
          .eq("day_of_week", dow)
          .eq("status", "active")
          .lte("valid_from", dateStr)
          .gte("valid_until", dateStr);

        days.push({
          day: format(d, "EEE", { locale: sv }),
          count: (bookingCount ?? 0) + (subCount ?? 0),
        });
      }
      return days;
    },
    enabled: !!hallId,
  });

  // PR-AT — Recent activity feed via SECURITY DEFINER fn that UNIONs 4
  // sources (court bookings / aktiviteter / träning / open match-deltagare),
  // sorted by created_at DESC. Replaces the previous bookings-only query
  // that sorted by start_time DESC (which surfaced upcoming bookings, not
  // truly "just-created" rows — confusing for admin who wants to see
  // recent activity at a glance).
  //
  // Earlier fix (Dashboard-Rapporter Fel B) avoided created_at because
  // batched old-data inserts surfaced stale rows. That concern is moot
  // here — the new fn aggregates by ACTUAL row-creation timestamps from
  // each source table, and the UNION strategy means upstream batch-insert
  // anomalies don't favor one source over another.
  type RecentTypeFilter = 'all' | 'court' | 'activity' | 'training' | 'match_player';
  const [recentTypeFilter, setRecentTypeFilter] = useState<RecentTypeFilter>('all');

  const { data: recentBookings = [], isLoading: loadingRecent } = useQuery({
    queryKey: ["mgr-recent-activity", organization?.id, recentTypeFilter],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_recent_activity" as any, {
        p_org_id: organization!.id,
        p_limit: 20,
        p_type_filter: recentTypeFilter === 'all' ? null : recentTypeFilter,
      });
      if (error) throw error;
      return (data ?? []) as Array<{
        source_id: string;
        source_table: string;
        type_label: string;
        created_at: string;
        start_time: string;
        end_time: string | null;
        court_id: string | null;
        court_name: string | null;
        display_name: string;
        display_role: string;
        status: string;
        organization_id: string;
      }>;
    },
    enabled: !!organization?.id,
  });

  const kpis = [
    {
      label: t("dashboardPage.bookingsToday"),
      value: String(totalBookingsToday),
      icon: CalendarDays,
      color: "text-primary",
      link: "/manager/dashboard/bookings-today",
      trend: totalBookingsToday - bookingsYesterday,
      trendLabel: t("dashboardPage.vsYesterday"),
    },
    {
      label: t("dashboardPage.revenueThisMonth"),
      value: formatPrice(monthRevenue),
      icon: TrendingUp,
      color: "text-primary",
      link: "/manager/dashboard/revenue",
      trend: revenueTrendPct,
      trendLabel: t("dashboardPage.vsPrevMonth"),
    },
    {
      label: t("dashboardPage.unpaid"),
      value: String(unpaidCount),
      icon: CreditCard,
      color: unpaidCount > 0 ? "text-destructive" : "text-primary",
      link: "/manager/dashboard/unpaid",
      trend: 0,
      trendLabel: "",
    },
    {
      label: t("dashboardPage.occupancyToday"),
      value: occupancyPct != null ? `${occupancyPct}%` : "—",
      icon: BarChart3,
      color: "text-muted-foreground",
      link: "/manager/dashboard/occupancy",
      trend: 0,
      trendLabel: "",
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t("dashboardPage.title")}</h1>

      {/* KPI Widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <Link key={kpi.label} to={kpi.link}>
            <Card className="cursor-pointer hover:shadow-md transition-shadow group">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <kpi.icon className={cn("h-4 w-4", kpi.color)} />
                    <span className="text-xs">{kpi.label}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-2xl font-bold">{kpi.value}</p>
                {kpi.trendLabel && (
                  <TrendBadge value={kpi.trend} label={kpi.trendLabel} />
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick actions */}
      <div className="flex gap-2 flex-wrap">
        <Button asChild variant="outline" size="sm">
          <Link to="/manager/schedule">
            <Plus className="h-4 w-4 mr-1" />
            {t("dashboardPage.newBooking")}
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          {/* T2v2: "Ny aktivitet" opens the canonical wizard (was the Träning
              admin TAB — not a create flow at all). Admin wizard with
              templates/mass-creation is the post-blitz project (todo chip). */}
          <Link to="/create-activity">
            <Plus className="h-4 w-4 mr-1" />
            {t("dashboardPage.newActivity")}
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to="/manager/schedule">
            <CalendarRange className="h-4 w-4 mr-1" />
            {t("dashboardPage.viewSchedule")}
          </Link>
        </Button>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-6">
        <div className="space-y-6">
          {/* Recent bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("dashboardPage.recentBookings")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingRecent ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : (
                <>
                  {/* PR-AT: pill filter for activity-type. Matches AdminCustomers
                      CategoryFilterBadges visual pattern. */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {([
                      { v: 'all', label: t("dashboardPage.recentFilterAll", { defaultValue: 'Alla' }) },
                      { v: 'court', label: t("dashboardPage.recentFilterCourt", { defaultValue: 'Strö' }) },
                      { v: 'activity', label: t("dashboardPage.recentFilterActivity", { defaultValue: 'Aktiviteter' }) },
                      { v: 'training', label: t("dashboardPage.recentFilterTraining", { defaultValue: 'Träning' }) },
                      { v: 'match_player', label: t("dashboardPage.recentFilterMatchPlayer", { defaultValue: 'Open match-deltagare' }) },
                    ] as Array<{ v: RecentTypeFilter; label: string }>).map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => setRecentTypeFilter(opt.v)}
                        className={cn(
                          "text-xs px-2.5 py-1 rounded-full border transition-colors",
                          recentTypeFilter === opt.v
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:bg-muted"
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="pb-2 font-medium">{t("dashboardPage.recentName", { defaultValue: 'Namn' })}</th>
                          <th className="pb-2 font-medium">{t("dashboardPage.recentType", { defaultValue: 'Typ' })}</th>
                          <th className="pb-2 font-medium">{t("dashboardPage.time")}</th>
                          <th className="pb-2 font-medium">{t("dashboardPage.court")}</th>
                          <th className="pb-2 font-medium">{t("dashboardPage.status")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentBookings.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="py-4 text-center text-muted-foreground text-xs">
                              {t("dashboardPage.recentEmpty", { defaultValue: 'Inga aktiviteter att visa' })}
                            </td>
                          </tr>
                        ) : recentBookings.map((b) => (
                          <tr key={`${b.source_table}-${b.source_id}`} className="border-b last:border-0">
                            <td className="py-2">
                              <div className="font-medium">{b.display_name}</div>
                              <div className="text-[10px] text-muted-foreground">{b.display_role}</div>
                            </td>
                            <td className="py-2">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                                {b.type_label}
                              </span>
                            </td>
                            <td className="py-2 text-xs">
                              {format(new Date(b.start_time), "d MMM HH:mm", { locale: sv })}
                            </td>
                            <td className="py-2 text-xs">{b.court_name ?? "—"}</td>
                            <td className="py-2">
                              <span className={cn(
                                "text-xs px-2 py-0.5 rounded-full font-medium",
                                b.status === "confirmed" ? "bg-primary/10 text-primary" :
                                b.status === "cancelled" ? "bg-destructive/10 text-destructive" :
                                "bg-muted text-muted-foreground"
                              )}>
                                {b.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Week chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("dashboardPage.weekChart")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekData}>
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        {/* V2 Issue 5.2: Occupancy-panelen togs bort här. Samma metric visas
         *  redan via KPI-kortet högst upp på Dashboard (som också länkar till
         *  /manager/dashboard/occupancy) — att ha den två gånger på samma
         *  skärm var förvirrande. Unpaid-panelen behålls eftersom den har en
         *  "Visa alla"-CTA som KPI-kortet saknar. */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("dashboardPage.unpaid")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{unpaidCount}</p>
                  <p className="text-sm text-muted-foreground">{t("dashboardPage.pendingPayments")}</p>
                </div>
                <Button asChild variant="outline" size="sm">
                  <Link to="/manager/dashboard/unpaid">
                    {t("dashboardPage.viewAll")}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
